import { Activity, CheckCircle2, Clock3, Target } from "lucide-react";
import { Link } from "react-router";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { useCrmActionEffectiveness } from "@/hooks/use-crm-action-effectiveness";
import { formatMonthDay } from "@/lib/i18n";
import type { CrmActionEffectivenessNamedBucket } from "@/hooks/use-crm-action-effectiveness";

function prettyActionLabel(value: string | null | undefined): string {
  if (!value || value === "unknown") {
    return "Unknown action";
  }

  return value
    .replace(/^crm\./, "")
    .replace(/@v\d+$/, "")
    .replace(/[._-]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function percent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function bucketLabel(bucket: CrmActionEffectivenessNamedBucket | undefined): string {
  return prettyActionLabel(bucket?.actionRef ?? bucket?.intent ?? bucket?.sourceEventType ?? bucket?.signalFamily ?? bucket?.sourceSurface);
}

function targetLinkForMaterialization(item: {
  contactId: string | null;
  targetEntityType?: string | null;
  targetEntityId?: string | null;
}): { to: string; label: string } | null {
  if (item.contactId) {
    return { to: `/crm/contacts/${item.contactId}`, label: "Contact" };
  }
  if (item.targetEntityType === "deal" && item.targetEntityId) {
    return { to: `/crm/deals/${item.targetEntityId}`, label: "Deal" };
  }
  if (item.targetEntityType === "company" && item.targetEntityId) {
    return { to: `/crm/companies/${item.targetEntityId}`, label: "Company" };
  }
  if (item.targetEntityType === "task") {
    return { to: "/crm/tasks", label: "Task" };
  }
  if (item.targetEntityType === "activity") {
    return { to: "/crm/activities", label: "Activity" };
  }
  return null;
}

interface ActionEffectivenessSummaryProps {
  contractRef?: string;
  signalFamily?: string;
  sourceEventType?: string;
}

export function ActionEffectivenessSummary({
  contractRef,
  signalFamily,
  sourceEventType,
}: ActionEffectivenessSummaryProps = {}) {
  const effectivenessFilters = {
    ...(contractRef ? { actionRef: contractRef } : {}),
    ...(signalFamily ? { signalFamily } : {}),
    ...(sourceEventType ? { sourceEventType } : {}),
  };
  const { data, isLoading, error, refetch } = useCrmActionEffectiveness(effectivenessFilters);

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
        <div className="mb-4 flex items-center gap-2">
          <Skeleton className="h-5 w-5" />
          <Skeleton className="h-4 w-52" />
        </div>
        <SkeletonCard className="h-24" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 p-4 text-sm text-[var(--color-warning)]">
        Could not load action effectiveness.
        <button type="button" onClick={refetch} className="ml-2 underline">Retry</button>
      </div>
    );
  }

  const totals = data?.totals ?? {
    reviewed: 0,
    accepted: 0,
    dismissed: 0,
    materialized: 0,
    crmMaterialized: 0,
    manualResolved: 0,
    acceptanceRate: 0,
    materializationRate: 0,
    crmMaterializationRate: 0,
    manualResolutionRate: 0,
    avgTimeToMaterializeMinutes: null,
  };
  const topAction = data?.byActionRef[0];
  const topIntent = data?.byIntent[0];
  const topSourceSurface = data?.bySourceSurface[0];
  const topSourceEventType = data?.bySourceEventType?.[0];
  const topSignalFamily = data?.bySignalFamily?.[0];
  const signalFamilies = data?.bySignalFamily?.slice(0, 4) ?? [];
  const recent = data?.recentMaterializations.slice(0, 4) ?? [];

  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-[var(--color-primary)]" />
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Action Effectiveness</h2>
            <span className="rounded-full border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 px-1.5 py-0.5 text-[10px] text-[var(--color-success)]">
              Outcome loop
            </span>
          </div>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Tracks whether reviewed recommendations become accepted and materialized CRM outcomes.
          </p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-muted-foreground)]">
          {data?.windowDays ?? 30}d
        </span>
      </div>

      {contractRef && (
        <p className="mb-3 rounded-md border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-3 py-2 text-xs text-[var(--color-primary)]">
          Effectiveness scoped to {prettyActionLabel(contractRef)}
        </p>
      )}
      {(signalFamily || sourceEventType) && (
        <p className="mb-3 rounded-md border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-3 py-2 text-xs text-[var(--color-primary)]">
          Signal scope {[
            signalFamily ? `${prettyActionLabel(signalFamily)} family` : null,
            sourceEventType ? prettyActionLabel(sourceEventType) : null,
          ].filter(Boolean).join(" · ")}
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
          <p className="text-xs text-[var(--color-muted-foreground)]">Reviewed</p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-foreground)]">{totals.reviewed}</p>
        </div>
        <div className="rounded-xl border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 p-3">
          <p className="flex items-center gap-1 text-xs text-[var(--color-success)]">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Accepted
          </p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-success)]">{percent(totals.acceptanceRate)}</p>
        </div>
        <div className="rounded-xl border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 p-3">
          <p className="flex items-center gap-1 text-xs text-[var(--color-primary)]">
            <Target className="h-3.5 w-3.5" />
            Materialized
          </p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-primary)]">{percent(totals.materializationRate)}</p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {totals.crmMaterialized} CRM writes · {totals.manualResolved} manual
          </p>
        </div>
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
          <p className="flex items-center gap-1 text-xs text-[var(--color-muted-foreground)]">
            <Clock3 className="h-3.5 w-3.5" />
            Avg time
          </p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-foreground)]">
            {totals.avgTimeToMaterializeMinutes == null ? "-" : `${totals.avgTimeToMaterializeMinutes}m`}
          </p>
        </div>
      </div>

      {(topAction || topIntent || topSourceSurface || topSourceEventType || topSignalFamily) && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          {topAction && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
              <p className="text-xs text-[var(--color-muted-foreground)]">Best materialized action</p>
              <p className="mt-1 text-sm font-medium text-[var(--color-foreground)]">{bucketLabel(topAction)}</p>
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                {topAction.materialized}/{topAction.accepted} accepted materialized
              </p>
            </div>
          )}
          {topIntent && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
              <p className="text-xs text-[var(--color-muted-foreground)]">Best materialized intent</p>
              <p className="mt-1 text-sm font-medium text-[var(--color-foreground)]">{bucketLabel(topIntent)}</p>
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                {percent(topIntent.materializationRate)} materialization rate
              </p>
            </div>
          )}
          {topSourceEventType && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
              <p className="text-xs text-[var(--color-muted-foreground)]">Best source event</p>
              <p className="mt-1 text-sm font-medium text-[var(--color-foreground)]">{bucketLabel(topSourceEventType)}</p>
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                {topSourceEventType.materialized}/{topSourceEventType.accepted} accepted materialized
              </p>
            </div>
          )}
          {topSignalFamily && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
              <p className="text-xs text-[var(--color-muted-foreground)]">Best signal family</p>
              <p className="mt-1 text-sm font-medium text-[var(--color-foreground)]">{bucketLabel(topSignalFamily)}</p>
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                {percent(topSignalFamily.materializationRate)} materialization rate
              </p>
            </div>
          )}
          {topSourceSurface && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
              <p className="text-xs text-[var(--color-muted-foreground)]">Best source surface</p>
              <p className="mt-1 text-sm font-medium text-[var(--color-foreground)]">{bucketLabel(topSourceSurface)}</p>
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                {topSourceSurface.materialized}/{topSourceSurface.accepted} accepted materialized
              </p>
            </div>
          )}
        </div>
      )}

      {signalFamilies.length > 0 && (
        <div className="mt-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
          <p className="text-xs font-semibold text-[var(--color-muted-foreground)]">Signal family performance</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {signalFamilies.map((bucket) => (
              <div
                key={bucket.signalFamily ?? "unknown"}
                className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-xs"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium text-[var(--color-foreground)]">{bucketLabel(bucket)}</p>
                  <span className="text-[var(--color-primary)]">{percent(bucket.materializationRate)}</span>
                </div>
                <p className="mt-1 text-[var(--color-muted-foreground)]">
                  {bucket.materialized}/{bucket.accepted} accepted materialized · {bucket.reviewed} reviewed
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {recent.length > 0 && (
        <div className="mt-4 space-y-2">
          <p className="text-xs font-semibold text-[var(--color-muted-foreground)]">Recent materialized outcomes</p>
          {recent.map((item) => {
            const targetLink = targetLinkForMaterialization(item);

            return (
              <div
                key={item.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 text-xs"
              >
                <div className="min-w-0">
                  <p className="font-medium text-[var(--color-foreground)]">{prettyActionLabel(item.actionRef)}</p>
                  <p className="text-[var(--color-muted-foreground)]">
                    {item.manualResolution
                      ? "manual resolution recorded"
                      : `${item.materializedEntityType ?? "record"} ${item.materializationStatus ?? "done"}`} from {prettyActionLabel(item.sourceSurface)}
                    {item.sourceEventType ? ` · ${prettyActionLabel(item.sourceEventType)}` : ""}
                    {item.signalFamily ? ` · ${prettyActionLabel(item.signalFamily)} family` : ""}
                    {" - "}
                    {formatMonthDay(item.materializedAt)}
                  </p>
                </div>
                {targetLink && (
                  <Link
                    to={targetLink.to}
                    className="text-[var(--color-primary)] hover:underline"
                  >
                    {targetLink.label}
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
